// Detail pane → Settings. Replaces SettingsModal: the same nine flags, grouped
// into three sections instead of one flat list, and saved in place of a modal.

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Link,
  Select,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Chatbot, UpdateSettingsDto } from '@/lib/sdk/chatbot/types';
import { useDepartments } from '../../../hooks/useDepartments';
import { flowChatNotes } from '../flowState';

interface SettingsTabProps {
  chatbot: Chatbot;
  isSaving: boolean;
  onSave: (settings: UpdateSettingsDto) => void;
}

function toSettings(chatbot: Chatbot): UpdateSettingsDto {
  return {
    enable_quick_bot: chatbot.quick_bot,
    enable_raw_data_sync: chatbot.raw_data_sync,
    add_users_to_team: chatbot.add_users_to_team,
    data_editing_enabled: chatbot.data_editing_enabled,
    web_channel_enabled: chatbot.web_channel_enabled,
    reactions_enabled: chatbot.reactions_enabled,
    flows_enabled: chatbot.flows_enabled,
    batch_mode: chatbot.batch_mode,
    enable_summary: chatbot.enable_summary,
    flow_fields_per_screen: chatbot.flow_fields_per_screen ?? null,
    flow_hold_draft: chatbot.flow_hold_draft ?? false,
    department_key: chatbot.department_key ?? '',
    language: chatbot.language ?? 'en',
  };
}

/** Matches platform.FlowMaxFieldsPerScreenDefault / FlowMaxFieldsPerScreenLimit. */
const FIELDS_PER_SCREEN_DEFAULT = 5;
const FIELDS_PER_SCREEN_MAX = 10;

/** Only the boolean flags render as switches — department_key is a Select, not a toggle. */
type BooleanSettingKey = {
  [K in keyof UpdateSettingsDto]-?: NonNullable<UpdateSettingsDto[K]> extends boolean ? K : never;
}[keyof UpdateSettingsDto];

interface SettingRow {
  field: BooleanSettingKey;
  label: string;
  help: string;
}

const GROUPS: Array<{ title: string; rows: SettingRow[] }> = [
  {
    title: 'Conversation',
    rows: [
      {
        field: 'enable_quick_bot',
        label: 'Quick bot format',
        help: 'Enable quick response format for this bot',
      },
      {
        field: 'reactions_enabled',
        label: 'Reaction messages',
        help: 'Send a 👍 reaction on every user input, in addition to the typing indicator',
      },
      {
        field: 'web_channel_enabled',
        label: 'Web channel',
        help: 'Allow starting workflows via the web interface',
      },
    ],
  },
  {
    title: 'Data',
    rows: [
      {
        field: 'enable_raw_data_sync',
        label: 'Data sync',
        help: 'Enable excel data sync',
      },
      {
        field: 'add_users_to_team',
        label: 'Add users to team',
        help: 'Automatically add users to the team',
      },
    ],
  },
  {
    title: 'WhatsApp Flows',
    rows: [
      {
        field: 'flows_enabled',
        label: 'WhatsApp Flows',
        help: 'Allow bulk data entry via WhatsApp Flows instead of one message per field',
      },
      {
        field: 'enable_summary',
        label: 'Send summary on finish',
        help: 'Send the workflow completion message when a Flow session finishes',
      },
      {
        field: 'batch_mode',
        label: 'Batch mode',
        help: 'Automatically start a new Flow session after Finish',
      },
      {
        field: 'flow_hold_draft',
        label: 'Hold in draft',
        help: 'For demos and testing: automatic syncs keep this Flow in draft and never publish it. Switch off to publish. A Flow that is already live stays as it is while held, and runs in chat after you change its tasks until you switch this off.',
      },
    ],
  },
];

const SYNC_OFF_WARNING = '⚠️ Disabling data sync will effect excel sheet data analysis';

export function SettingsTab({ chatbot, isSaving, onSave }: SettingsTabProps) {
  const [settings, setSettings] = useState<UpdateSettingsDto>(() => toSettings(chatbot));
  const { departments } = useDepartments(chatbot.org_id);

  // Reset whenever a different workflow is selected, or the saved values change.
  useEffect(() => {
    setSettings(toSettings(chatbot));
  }, [chatbot]);

  const saved = toSettings(chatbot);
  const isDirty = (Object.keys(settings) as Array<keyof UpdateSettingsDto>).some(
    (field) => settings[field] !== saved[field]
  );

  const chatNotes = flowChatNotes(chatbot);

  const helpFor = (row: SettingRow) =>
    row.field === 'enable_raw_data_sync' && !settings.enable_raw_data_sync
      ? SYNC_OFF_WARNING
      : row.help;

  return (
    <VStack align="stretch" spacing={6} maxW="640px">
      <Box>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="text-secondary"
          mb={3}
        >
          Department
        </Text>
        <FormControl>
          <Select
            value={settings.department_key ?? ''}
            onChange={(event) =>
              setSettings((current) => ({ ...current, department_key: event.target.value }))
            }
            data-testid="wfl-settings-department-select"
            bg="bg-surface"
            maxW="320px"
            aria-label="Department"
          >
            <option value="">No department</option>
            {departments.map((department) => (
              <option key={department.key} value={department.key}>
                {department.label}
              </option>
            ))}
          </Select>
          <Text fontSize="xs" color="text-secondary" mt={2}>
            The department list is shared across workflows. Manage it in{' '}
            <Link as={RouterLink} to={`/dashboard/${chatbot.org_id}/master-data`} color="brand.500">
              Master Data
            </Link>
            .
          </Text>
        </FormControl>
        <Divider mt={5} />
      </Box>

      <Box>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="text-secondary"
          mb={3}
        >
          Language
        </Text>
        <FormControl>
          <Select
            value={settings.language ?? 'en'}
            onChange={(event) =>
              setSettings((current) => ({
                ...current,
                language: event.target.value as 'en' | 'hi',
              }))
            }
            data-testid="wfl-settings-language-select"
            bg="bg-surface"
            maxW="320px"
            aria-label="Language"
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
          </Select>
          <Text fontSize="xs" color="text-secondary" mt={2}>
            Language for bot-sent messages that support translation — currently just the
            document verification rejection prompt. The rest of the bot&apos;s messages stay
            in English regardless of this setting.
          </Text>
        </FormControl>
        <Divider mt={5} />
      </Box>

      {GROUPS.map((group) => (
        <Box key={group.title}>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="wider"
            color="text-secondary"
            mb={3}
          >
            {group.title}
          </Text>
          {group.title === 'WhatsApp Flows' && chatNotes.length > 0 && (
            <Box
              data-testid="wfl-settings-flow-chat-notes"
              borderWidth="1px"
              borderColor="orange.300"
              bg="bg-surface"
              borderRadius="md"
              px={3}
              py={2}
              mb={4}
            >
              {chatNotes.map((note) => (
                <Text key={note} fontSize="sm" color="text-primary">
                  {note}
                </Text>
              ))}
            </Box>
          )}
          <VStack align="stretch" spacing={4}>
            {group.rows.map((row) => {
              const controlId = `workflow-setting-${row.field}`;
              return (
                <FormControl
                  key={row.field}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <FormLabel htmlFor={controlId} mb={0}>
                    <Text fontWeight="medium" color="text-primary" fontSize="sm">
                      {row.label}
                    </Text>
                    <Text fontSize="xs" color="text-secondary">{helpFor(row)}</Text>
                  </FormLabel>
                  <Switch
                    data-testid={`wfl-settings-${row.field}`}
                    id={controlId}
                    colorScheme="brand"
                    isChecked={settings[row.field]}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        [row.field]: event.target.checked,
                      }))
                    }
                  />
                </FormControl>
              );
            })}
            {group.title === 'WhatsApp Flows' && (
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel htmlFor="workflow-setting-flow_fields_per_screen" mb={0}>
                  <Text fontWeight="medium" color="text-primary" fontSize="sm">
                    Fields per screen
                  </Text>
                  <Text fontSize="xs" color="text-secondary">
                    How many text, number, date and choice fields share one Flow screen.
                    Media, checklist and dynamic choice fields always get their own. Applies
                    on the next Flow sync.
                  </Text>
                </FormLabel>
                <Select
                  data-testid="wfl-settings-flow_fields_per_screen"
                  id="workflow-setting-flow_fields_per_screen"
                  bg="bg-surface"
                  maxW="140px"
                  flexShrink={0}
                  value={settings.flow_fields_per_screen ?? ''}
                  onChange={(event) =>
                    setSettings((current) => ({
                      ...current,
                      flow_fields_per_screen: event.target.value
                        ? Number(event.target.value)
                        : null,
                    }))
                  }
                >
                  <option value="">Default ({FIELDS_PER_SCREEN_DEFAULT})</option>
                  {Array.from({ length: FIELDS_PER_SCREEN_MAX }, (_, index) => index + 1).map(
                    (count) => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    )
                  )}
                </Select>
              </FormControl>
            )}
          </VStack>
          <Divider mt={5} />
        </Box>
      ))}

      <HStack justify="flex-end">
        <Button
          variant="ghost"
          onClick={() => setSettings(toSettings(chatbot))}
          isDisabled={!isDirty || isSaving}
        >
          Cancel
        </Button>
        <Button
          colorScheme="brand"
          data-testid="wfl-settings-save-btn"
          onClick={() => onSave(settings)}
          isLoading={isSaving}
          loadingText="Saving"
          isDisabled={!isDirty}
        >
          Save changes
        </Button>
      </HStack>
    </VStack>
  );
}
